import React, { useState } from "react";
import { motion } from "framer-motion";
import { toast } from "sonner";
import { RefreshCw, RotateCw, Check, X, Repeat } from "lucide-react";
import api, { imageUrl } from "@/lib/api";
import AnalyzingSequence from "@/components/AnalyzingSequence";
import { MoireSymbol } from "@/components/MoireLogo";

const SLOTS = ["wall", "rug", "coffee_table", "lighting", "cushions", "curtains", "art", "accent"];
const SLOT_LABEL = { wall: "Wall", rug: "Rug", coffee_table: "Table", lighting: "Lighting",
  cushions: "Cushions", curtains: "Curtains", art: "Art", accent: "Accent" };
const CYCLE = { change: "Change", keep: "Keep", remove: "Remove" };
const NEXT = { change: "keep", keep: "remove", remove: "change" };
const VIZ_STAGES = ["Reading your room…", "Placing the pieces…", "Styling the space…"];

// Complete The Room board + SEE THE ROOM visualisation. Reused by Style-A-Piece and Room Check.
export default function CompleteAndSeeRoom({ mode, checkId, source, directions }) {
  const [dirIdx, setDirIdx] = useState(0);
  const [slotState, setSlotState] = useState({}); // slotKey -> change|keep|remove
  const [viz, setViz] = useState({ status: "idle", image_url: null, disclaimer: "" });
  const [stale, setStale] = useState(false);

  const active = directions[dirIdx];

  const setDirection = (i) => { setDirIdx(i); setSlotState({}); if (viz.status === "ready") setStale(true); };
  const cycleSlot = (k) => {
    setSlotState((s) => ({ ...s, [k]: NEXT[s[k] || "change"] }));
    if (viz.status === "ready") setStale(true);
  };

  const seeTheRoom = async () => {
    setViz((v) => ({ ...v, status: "loading" })); setStale(false);
    const keeps = SLOTS.filter((k) => slotState[k] === "keep").map((k) => `${SLOT_LABEL[k]} — ${active.slots[k]?.name}`);
    const removes = SLOTS.filter((k) => slotState[k] === "remove" || slotState[k] === "keep");
    try {
      const { data } = await api.post("/home/room/visualise", {
        mode, check_id: checkId, completion: active, source, keeps, removes });
      if (data.available && data.image_url) {
        setViz({ status: "ready", image_url: data.image_url, disclaimer: data.disclaimer }); setStale(false);
      } else {
        setViz({ status: "unavailable", image_url: null, disclaimer: data.disclaimer });
      }
    } catch {
      setViz({ status: "unavailable", image_url: null, disclaimer: "The room preview isn't available right now, but your styling board is ready." });
    }
  };

  return (
    <div className="mt-10">
      <p className="font-sans text-[10px] tracking-luxe uppercase text-[#C8A97E]">Complete The Room</p>

      {/* directions */}
      <div className="flex gap-2 overflow-x-auto mt-3 pb-1" data-testid="home-directions">
        {directions.map((d, i) => (
          <button key={d.direction} onClick={() => setDirection(i)} data-testid={`home-direction-${d.direction}`}
            className={`shrink-0 px-4 py-2.5 font-sans text-[10px] tracking-luxe uppercase border transition-colors ${i === dirIdx ? "border-[#C8A97E] bg-[#111111] text-[#F5F2EA]" : "border-[#F5F2EA]/12 text-[#8A8170] hover:border-[#C8A97E]/50"}`}>
            <span className="text-[#C8A97E] mr-2">{String(i + 1).padStart(2, "0")}</span>{d.name}
          </button>
        ))}
      </div>

      <div className="grid lg:grid-cols-2 gap-8 mt-6">
        {/* SEE THE ROOM hero */}
        <div data-testid="see-the-room">
          <p className="font-sans text-[10px] tracking-luxe uppercase text-[#C8A97E] mb-2">See The Room</p>
          <div className="relative aspect-[4/3] border border-[#F5F2EA]/10 bg-gradient-to-br from-[#141414] to-[#0F0F0F] overflow-hidden flex items-center justify-center">
            {viz.status === "ready" && viz.image_url && (
              <>
                <img src={imageUrl(viz.image_url)} alt="Styled room" className="w-full h-full object-cover" data-testid="see-the-room-image" />
                <span className="absolute top-3 left-3 px-2.5 py-1 bg-[#0D0D0D]/70 backdrop-blur-sm font-sans text-[8px] tracking-luxe uppercase text-[#C8A97E]">Style Visualisation</span>
                {stale && (
                  <div className="absolute inset-x-0 bottom-0 bg-[#0D0D0D]/85 backdrop-blur-sm p-3 flex items-center justify-between gap-3" data-testid="room-stale">
                    <span className="font-sans text-[10px] text-[#F5F2EA]/80">Based on your previous edit</span>
                    <button onClick={seeTheRoom} data-testid="update-the-room" className="flex items-center gap-1.5 bg-[#C8A97E] text-[#0D0D0D] px-3 py-1.5 font-sans text-[10px] tracking-luxe uppercase hover:bg-[#E5C293] transition-colors"><RefreshCw size={12} /> Update The Room</button>
                  </div>
                )}
              </>
            )}
            {viz.status === "loading" && <div className="absolute inset-0 flex items-center justify-center" data-testid="room-loading"><AnalyzingSequence stages={VIZ_STAGES} /></div>}
            {(viz.status === "idle" || viz.status === "unavailable") && (
              <div className="text-center px-8">
                <MoireSymbol size={30} />
                {viz.status === "unavailable"
                  ? <p className="font-serif italic text-base text-[#F5F2EA]/70 mt-4">{viz.disclaimer}</p>
                  : <button onClick={seeTheRoom} data-testid="see-the-room-btn" className="mt-5 inline-flex items-center gap-2 bg-[#C8A97E] text-[#0D0D0D] px-7 py-3 font-sans text-xs tracking-luxe uppercase hover:bg-[#E5C293] transition-colors"><RotateCw size={14} /> See The Room</button>}
              </div>
            )}
          </div>
          {viz.status === "ready" && <p className="font-sans text-[10px] text-[#8A8170] mt-2 italic">{viz.disclaimer}</p>}
          <p className="font-sans text-[10px] text-[#8A8170] mt-2">{mode === "room" ? "MOIRÉ edits your actual room — architecture kept, styling reimagined." : "A styled room built around your piece."}</p>
        </div>

        {/* structured board + why */}
        <div>
          <div className="divide-y divide-[#F5F2EA]/8 border-y border-[#F5F2EA]/8" data-testid="room-board">
            <div className="flex items-center gap-3 py-3">
              <span className="h-8 w-8 rounded-full border border-[#C8A97E]/60" style={{ backgroundColor: active.slots.source?.hex }} />
              <div className="flex-1"><div className="font-serif text-lg text-[#F5F2EA]">{active.slots.source?.label}</div><div className="font-sans text-[9px] tracking-luxe uppercase text-[#C8A97E]">Your piece · kept</div></div>
              <Check size={15} className="text-[#C8A97E]" />
            </div>
            {SLOTS.map((k) => active.slots[k] && (
              <div key={k} className={`flex items-center gap-3 py-3 ${slotState[k] === "remove" ? "opacity-40" : ""}`} data-testid={`room-slot-${k}`}>
                <span className="h-8 w-8 rounded-full border border-[#F5F2EA]/15" style={{ backgroundColor: active.slots[k].hex }} />
                <div className="flex-1 min-w-0">
                  <div className="font-serif text-lg text-[#F5F2EA] truncate">{active.slots[k].name}</div>
                  <div className="font-sans text-[9px] tracking-luxe uppercase text-[#8A8170]">{SLOT_LABEL[k]} · {active.slots[k].material}</div>
                </div>
                <button onClick={() => cycleSlot(k)} data-testid={`room-slot-cycle-${k}`}
                  className="font-sans text-[9px] tracking-luxe uppercase border border-[#F5F2EA]/15 px-2.5 py-1.5 text-[#C8A97E] hover:border-[#C8A97E] transition-colors w-[74px]">{CYCLE[slotState[k] || "change"]}</button>
              </div>
            ))}
          </div>
          <div className="mt-5">
            <p className="font-sans text-[10px] tracking-luxe uppercase text-[#8A8170] mb-1">Why this room works</p>
            <p className="font-serif italic text-lg text-[#F5F2EA]/85 leading-relaxed" data-testid="room-why">{active.why}</p>
          </div>
          {viz.status === "ready" && (
            <button onClick={seeTheRoom} data-testid="restyle-room" className="mt-5 flex items-center gap-2 border border-[#C8A97E]/60 text-[#C8A97E] px-5 py-2.5 font-sans text-xs tracking-luxe uppercase hover:bg-[#C8A97E] hover:text-[#0D0D0D] transition-colors"><Repeat size={14} /> Regenerate This Room</button>
          )}
        </div>
      </div>
    </div>
  );
}
